package com.htmlagent.site.components.widgets

import androidx.compose.runtime.Composable
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.LayoutPrefs
import com.htmlagent.site.theme.Layout
import com.htmlagent.site.theme.ResizerGripStyle
import com.htmlagent.site.theme.ResizerStyle
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event
import org.w3c.dom.pointerevents.PointerEvent

/**
 * The divider that splits the width between the chat and the page, draggable and workable with
 * the arrows, Home and End.
 *
 * Dragging is flagged by hand and `buttons != 0` is required on top: `pointermove` also fires on
 * hover, and with capture alone the divider ran away from the cursor. The keyboard goes in the
 * attributes and not in `ref`, which runs once and would freeze `current` at the first mount.
 * Pointer capture keeps the moves coming even if the cursor leaves, with no `window` listeners
 * to remember to remove.
 */
@Composable
fun Resizer(current: Int, onResize: (Int) -> Unit) {
    val t = LocalStrings.current.artifact
    Div(
        ResizerStyle.toModifier().toAttrs {
            attr("role", "separator")
            attr("aria-orientation", "vertical")
            attr("aria-label", t.chatWidth)
            attr("aria-valuenow", current.toString())
            attr("aria-valuemin", MIN.toString())
            attr("aria-valuemax", max().toString())
            attr("tabindex", "0")

            onKeyDown { event ->
                val next = when (event.key) {
                    "ArrowLeft" -> current - STEP
                    "ArrowRight" -> current + STEP
                    "Home" -> MIN
                    "End" -> max()
                    else -> null
                }
                next?.let {
                    event.preventDefault()
                    onResize(clamp(it))
                }
            }

            ref { element ->
                var dragging = false

                val down: (Event) -> Unit = { event ->
                    val pointer = event as PointerEvent
                    dragging = true
                    element.setPointerCapture(pointer.pointerId)
                    // Without this the gesture reads as text selection and the pointer stops sending events.
                    event.preventDefault()
                }

                val move: (Event) -> Unit = { event ->
                    val pointer = event as PointerEvent
                    // `buttons == 0` is a move with nothing pressed: hover.
                    if (dragging && pointer.buttons.toInt() != 0) {
                        // Absolute position, not summed deltas: accumulating drifts after a few drags.
                        val left = element.parentElement?.getBoundingClientRect()?.left ?: 0.0
                        onResize(clamp((pointer.clientX - left).toInt()))
                    }
                }

                val end: (Event) -> Unit = { event ->
                    dragging = false
                    val pointer = event as? PointerEvent
                    if (pointer != null && element.hasPointerCapture(pointer.pointerId)) {
                        element.releasePointerCapture(pointer.pointerId)
                    }
                }

                element.addEventListener("pointerdown", down)
                element.addEventListener("pointermove", move)
                element.addEventListener("pointerup", end)
                element.addEventListener("pointercancel", end)
                // The browser may drop the capture on its own; without this `dragging` would stay `true`.
                element.addEventListener("lostpointercapture", end)

                onDispose {
                    element.removeEventListener("pointerdown", down)
                    element.removeEventListener("pointermove", move)
                    element.removeEventListener("pointerup", end)
                    element.removeEventListener("pointercancel", end)
                    element.removeEventListener("lostpointercapture", end)
                }
            }
        },
    ) {
        Div(ResizerGripStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
    }
}

/**
 * The bounds come from [LayoutPrefs], which is also what clamps the stored value: two copies of
 * a minimum are two chances for them to disagree, and the disagreement shows up as a panel the
 * divider cannot drag back out.
 */
private const val MIN = Layout.ChatMinWidth
private const val STEP = Layout.ChatWidthStep

private fun max(): Int = LayoutPrefs.maxChatWidth()

private fun clamp(value: Int): Int = LayoutPrefs.clamp(value)
